/**
 * Version + kind byte schema and positional guards.
 *
 * Every on-wire blob begins with `version (1B) ‖ kind (1B)`. Rejection of an
 * unknown version or kind happens BEFORE any ciphertext byte is read, so no
 * AEAD tag-check timing distinguishes "wrong version" from "wrong key".
 */
import { KindByteRejectedError, VersionByteRejectedError } from './errors.js';
export const VERSION_V1 = 0x01;
/**
 * Structural variant of a blob (byte 1). Envelope kinds wrap the UMK; item /
 * dek-wrap kinds carry per-item material. `EnvelopeMitIdReserved` is allocated
 * but never written in v1 (post-v1 migration target — INV-13).
 */
export var KindByte;
(function (KindByte) {
    KindByte[KindByte["EnvelopePassphrase"] = 1] = "EnvelopePassphrase";
    KindByte[KindByte["EnvelopeRecovery"] = 2] = "EnvelopeRecovery";
    KindByte[KindByte["EnvelopeDevice"] = 3] = "EnvelopeDevice";
    KindByte[KindByte["EnvelopeMitIdReserved"] = 4] = "EnvelopeMitIdReserved";
    KindByte[KindByte["ItemPayload"] = 16] = "ItemPayload";
    KindByte[KindByte["DekWrap"] = 17] = "DekWrap";
})(KindByte || (KindByte = {}));
const KNOWN_KINDS = new Set([
    KindByte.EnvelopePassphrase,
    KindByte.EnvelopeRecovery,
    KindByte.EnvelopeDevice,
    KindByte.EnvelopeMitIdReserved,
    KindByte.ItemPayload,
    KindByte.DekWrap,
]);
/**
 * Assert byte 0 is VERSION_V1. Throws {@link VersionByteRejectedError} for
 * `0x00`, `0x02`–`0xFE` (reserved v2+), and `0xFF` (debug-trap).
 */
export function assertVersion(versionByte) {
    if (versionByte !== VERSION_V1) {
        throw new VersionByteRejectedError();
    }
}
/**
 * Assert byte 1 is a known kind AND equals the value the call site expects.
 * Throws {@link KindByteRejectedError} otherwise.
 */
export function assertKind(kindByte, expected) {
    if (!KNOWN_KINDS.has(kindByte) || kindByte !== expected) {
        throw new KindByteRejectedError();
    }
}
/**
 * Parse and validate the `version ‖ kind` header of a blob without reading any
 * ciphertext. Returns the byte offset where ciphertext-region parsing may begin
 * (2). Centralises the "reject before AEAD" guarantee.
 */
export function assertHeader(blob, expectedKind) {
    // Minimum: version + kind + (smallest nonce 12) + tag 16 = 30. Cheap length sanity.
    if (blob.length < 2) {
        throw new VersionByteRejectedError();
    }
    assertVersion(blob[0]);
    assertKind(blob[1], expectedKind);
    return 2;
}
//# sourceMappingURL=version.js.map